"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import OpenGraphPreview, { type OpenGraphPreviewData } from "./OpenGraphPreview";
import ManifoldItemPreviewCard from "./ManifoldItemPreviewCard";
import { fetchLinkPreview, type LinkPreviewResponse } from "@/services/api/link-preview-api";
import { matchesDomainOrSubdomain } from "@/lib/url/domains";
interface MarketplacePreviewProps {
  readonly href: string;
  readonly imageOnly?: boolean | undefined;
}
type PreviewState = {
  readonly type: "loading";
  readonly href: string;
} | {
  readonly type: "success";
  readonly href: string;
  readonly data: LinkPreviewResponse;
} | {
  readonly type: "error";
  readonly href: string;
  readonly error: Error;
};
type PickedMedia = {
  readonly url: string;
  readonly mimeType: string;
};
const OPENSEA_HOST = "opensea.io";
const IMAGE_MIME_BY_EXTENSION: Record<string, string> = {
  avif: "image/avif",
  gif: "image/gif",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  png: "image/png",
  svg: "image/svg+xml",
  webp: "image/webp"
};
const asNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};
const toPreviewData = (response: LinkPreviewResponse): OpenGraphPreviewData => {
  return {
    ...response,
    image: response.image ?? undefined,
    images: response.images ?? undefined,
    url: response.url ?? response.requestUrl ?? undefined,
    siteName: response.siteName ?? undefined,
    description: response.description ?? undefined,
    title: response.title ?? undefined
  };
};
const isOpenSeaHref = (href: string): boolean => {
  try {
    const parsed = new URL(href);
    return matchesDomainOrSubdomain(parsed.hostname.toLowerCase(), OPENSEA_HOST);
  } catch {
    return false;
  }
};
const isBlockedOpenSeaOverlayUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    const path = parsed.pathname.toLowerCase();
    if (!matchesDomainOrSubdomain(host, OPENSEA_HOST)) {
      return false;
    }
    return path.includes("opengraph");
  } catch {
    return false;
  }
};
const inferMimeTypeFromUrl = (url: string): string | undefined => {
  try {
    const parsed = new URL(url);
    const extensionMatch = parsed.pathname.toLowerCase().match(/\.([a-z0-9]+)$/);
    if (!extensionMatch?.[1]) {
      return undefined;
    }
    return IMAGE_MIME_BY_EXTENSION[extensionMatch[1]];
  } catch {
    const path = url.split("?")[0]?.toLowerCase() ?? "";
    const extensionMatch = path.match(/\.([a-z0-9]+)$/);
    if (!extensionMatch?.[1]) {
      return undefined;
    }
    return IMAGE_MIME_BY_EXTENSION[extensionMatch[1]];
  }
};
const toPickedMedia = (candidate: {
  url?: string | null | undefined;
  secureUrl?: string | null | undefined;
  type?: string | null | undefined;
} | null | undefined): PickedMedia | undefined => {
  if (!candidate) {
    return undefined;
  }
  const urlCandidate = candidate.url ?? candidate.secureUrl;
  if (typeof urlCandidate !== "string" || urlCandidate.trim().length === 0) {
    return undefined;
  }
  const url = urlCandidate.trim();
  const mimeType = asNonEmptyString(candidate.type) ?? inferMimeTypeFromUrl(url) ?? "image/*";
  return {
    url,
    mimeType
  };
};
const pickMedia = (data: LinkPreviewResponse): PickedMedia | undefined => {
  const primary = toPickedMedia(data.image);
  if (primary) {
    return primary;
  }
  const images = data.images;
  if (images === null || images === undefined) {
    return undefined;
  }
  for (const image of images) {
    const candidate = toPickedMedia(image);
    if (candidate) {
      return candidate;
    }
  }
  return undefined;
};
const sanitizeOpenSeaOverlayMedia = (href: string, data: LinkPreviewResponse): LinkPreviewResponse => {
  if (!isOpenSeaHref(href)) {
    return data;
  }
  const primary = toPickedMedia(data.image);
  const hasBlockedPrimary = primary !== undefined && isBlockedOpenSeaOverlayUrl(primary.url);
  const hasNonBlockedPrimary = primary !== undefined && !hasBlockedPrimary;
  const images = data.images ?? [];
  let hasNonBlockedImageCandidate = false;
  const sanitizedImages = images.filter(image => {
    const candidate = toPickedMedia(image);
    if (!candidate) {
      return true;
    }
    if (isBlockedOpenSeaOverlayUrl(candidate.url)) {
      return false;
    }
    hasNonBlockedImageCandidate = true;
    return true;
  });
  const hasAnyNonBlockedCandidate = hasNonBlockedPrimary || hasNonBlockedImageCandidate;
  if (!hasAnyNonBlockedCandidate) {
    return data;
  }
  const didChangeImages = sanitizedImages.length !== images.length;
  if (!hasBlockedPrimary && !didChangeImages) {
    return data;
  }
  return {
    ...data,
    image: hasBlockedPrimary ? null : data.image,
    images: sanitizedImages
  };
};
export default function MarketplacePreview(t0) {
  const $ = _c(24);
  const {
    href,
    imageOnly: t1
  } = t0;
  const imageOnly = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== href) {
    t2 = {
      type: "loading",
      href
    };
    $[0] = href;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [state, setState] = useState(t2);
  let t3;
  let t4;
  if ($[2] !== href) {
    t3 = () => {
      let active = true;
      const loadPreview = async () => {
        ;
        try {
          const response = sanitizeOpenSeaOverlayMedia(href, await fetchLinkPreview(href));
          if (!active) {
            return;
          }
          setState({
            type: "success",
            href,
            data: response
          });
        } catch (t5) {
          const error = t5;
          if (active) {
            setState({
              type: "error",
              href,
              error: error instanceof Error ? error : new Error("Failed to load marketplace preview")
            });
          }
        }
      };
      loadPreview();
      return () => {
        active = false;
      };
    };
    t4 = [href];
    $[2] = href;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  if (state.href !== href || state.type === "loading") {
    let t5;
    if ($[5] !== href || $[6] !== imageOnly) {
      t5 = <OpenGraphPreview href={href} preview={undefined} imageOnly={imageOnly} hideActions={imageOnly} />;
      $[5] = href;
      $[6] = imageOnly;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    return t5;
  }
  if (state.type === "error") {
    throw state.error;
  }
  let t5;
  if ($[8] !== state.data.title) {
    t5 = asNonEmptyString(state.data.title);
    $[8] = state.data.title;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const title = t5;
  let t6;
  if ($[10] !== state.data) {
    t6 = pickMedia(state.data);
    $[10] = state.data;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const media = t6;
  if (title && media) {
    let t7;
    if ($[12] !== href || $[13] !== imageOnly || $[14] !== media.mimeType || $[15] !== media.url || $[16] !== title) {
      t7 = <ManifoldItemPreviewCard href={href} title={title} mediaUrl={media.url} mediaMimeType={media.mimeType} imageOnly={imageOnly} hideActions={imageOnly} />;
      $[12] = href;
      $[13] = imageOnly;
      $[14] = media.mimeType;
      $[15] = media.url;
      $[16] = title;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    return t7;
  }
  let t7;
  if ($[18] !== state.data) {
    t7 = toPreviewData(state.data);
    $[18] = state.data;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== href || $[21] !== imageOnly || $[22] !== t7) {
    t8 = <OpenGraphPreview href={href} preview={t7} imageOnly={imageOnly} hideActions={imageOnly} />;
    $[20] = href;
    $[21] = imageOnly;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}